import type { Trace } from '@/features/traceability/type/Tool';
import type { FilterDimension, FilterOperator } from '../type/dynamicChart';

export type FilterDimensionKind = 'categorical' | 'numeric' | 'date';

export type FilterDimensionDef = {
  key: FilterDimension;
  labelKey: string;
  kind: FilterDimensionKind;
  unit?: string;
  operators: FilterOperator[];
  extract: (trace: Trace) => string | number | null;
  comparisonOnly?: boolean;
};

// Extend Trace to allow optional date and reference fields that may appear
// in future API versions or subclasses
type TraceExtended = Trace & {
  startedAt?: string | null;
  endedAt?: string | null;
  reference?: string | null;
};

export const filterDimensionRegistry: FilterDimensionDef[] = [
  {
    key: 'machine',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.machine',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => t.machine?.name ?? null,
  },
  {
    key: 'material',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.material',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => t.material?.name ?? null,
  },
  {
    key: 'toolState',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.toolState',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => t.toolState?.toolState ?? null,
  },
  {
    key: 'lubrication',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.lubrication',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => t.lubType ?? null,
  },
  {
    key: 'toolHolder',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.toolHolder',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => t.holderType ?? null,
  },
  {
    key: 'totalMachiningDuration',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.totalMachiningDuration',
    kind: 'numeric',
    unit: 's', // durations are stored and compared in seconds
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.totalMachiningDuration,
  },
  {
    key: 'pieceMachiningDuration',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.pieceMachiningDuration',
    kind: 'numeric',
    unit: 's', // durations are stored and compared in seconds
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.pieceMachiningDuration,
  },
  {
    key: 'machinedVolume',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.machinedVolume',
    kind: 'numeric',
    unit: 'cm³',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.machinedVolume,
  },
  {
    key: 'machinedDistance',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.machinedDistance',
    kind: 'numeric',
    unit: 'm',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.machinedDistance,
  },
  {
    key: 'machinedPieces',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.machinedPieces',
    kind: 'numeric',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.machinedPieces,
  },
  {
    key: 'malround',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.malround',
    kind: 'numeric',
    unit: 'mm',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.malround,
  },
  {
    key: 'operation',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.operation',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => t.operation ?? null,
  },
  {
    key: 'clampingType',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.clampingType',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => t.clampingType ?? null,
  },
  {
    key: 'vc',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.vc',
    kind: 'numeric',
    unit: 'm/min',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.vc,
  },
  {
    key: 'fz',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.fz',
    kind: 'numeric',
    unit: 'mm/z',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.fz,
  },
  {
    key: 'ap',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.ap',
    kind: 'numeric',
    unit: 'mm',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.ap,
  },
  {
    key: 'ae',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.ae',
    kind: 'numeric',
    unit: 'mm',
    operators: ['range', 'gte', 'lte'],
    extract: (t) => t.ae,
  },
  {
    key: 'startedAt',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.startedAt',
    kind: 'date',
    operators: ['date_range', 'after', 'before'],
    extract: (t) => (t as TraceExtended).startedAt ?? null,
  },
  {
    key: 'endedAt',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.endedAt',
    kind: 'date',
    operators: ['date_range', 'after', 'before'],
    extract: (t) => (t as TraceExtended).endedAt ?? null,
  },
  {
    key: 'reference',
    labelKey: 'analytics.dashboard.complexCharts.filters.dimensions.reference',
    kind: 'categorical',
    operators: ['in'],
    extract: (t) => (t as TraceExtended).reference ?? null,
    comparisonOnly: true,
  },
];
